import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';

import { ICON_STROKE_WIDTH } from '../../constants';
import {
  DEFAULT_MAX_RETRY_COUNT,
  DEFAULT_RETRY_INTERVAL_MINUTES,
  MAX_MAX_RETRY_COUNT,
  MAX_RETRY_INTERVAL_MINUTES,
  MIN_MAX_RETRY_COUNT,
  MIN_RETRY_INTERVAL_MINUTES,
} from '../../constants/notificationRetryConfig.constants';
import notificationRetryConfigService from '../../services/notificationRetryConfigService';
import type { NotificationRetryConfig } from '../../types/notificationRetryConfig.types';

interface NotificationRetryRecord {
  id: number;
  maxRetryCount: number;
  retryIntervalMinutes: number;
  updatedAt: string;
}

interface NotificationRetrySectionProps {
  facilityId: number;
}

function sanitizeIntegerInput(value: string): string {
  return value.replaceAll(/\D/g, '');
}

function parseRetryCount(value: string): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const count = Number(value);
  if (!Number.isInteger(count) || count < MIN_MAX_RETRY_COUNT || count > MAX_MAX_RETRY_COUNT) {
    return null;
  }
  return count;
}

function parseRetryInterval(value: string): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const minutes = Number(value);
  if (
    !Number.isInteger(minutes) ||
    minutes < MIN_RETRY_INTERVAL_MINUTES ||
    minutes > MAX_RETRY_INTERVAL_MINUTES
  ) {
    return null;
  }
  return minutes;
}

function mapApiRecord(row: NotificationRetryConfig): NotificationRetryRecord {
  return {
    id: row.id,
    maxRetryCount: row.maxRetryCount,
    retryIntervalMinutes: row.retryIntervalMinutes,
    updatedAt: row.updatedAt || row.createdAt || new Date().toISOString(),
  };
}

function blockInvalidNumericKeys(event: React.KeyboardEvent<HTMLInputElement>) {
  if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
    event.preventDefault();
  }
}

function NotificationRetryTableBody({
  loading,
  record,
  saving,
  onEdit,
  onDeleteRequest,
  onCreateFromDefaults,
}: Readonly<{
  loading: boolean;
  record: NotificationRetryRecord | null;
  saving: boolean;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onCreateFromDefaults: () => void;
}>) {
  if (loading) {
    return (
      <tr>
        <td
          colSpan={3}
          className="esc-table-empty"
        >
          Loading…
        </td>
      </tr>
    );
  }

  if (record) {
    return (
      <tr>
        <td>{record.maxRetryCount}</td>
        <td>{record.retryIntervalMinutes}</td>
        <td className="esc-table-actions-col esc-table-actions-col--wide">
          <div className="esc-table-actions">
            <button
              type="button"
              className="esc-icon-btn"
              onClick={onEdit}
              aria-label="Edit notification retry config"
              disabled={saving}
            >
              <Pencil
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
              />
              <span>Edit</span>
            </button>
            <button
              type="button"
              className="esc-icon-btn esc-icon-btn--danger"
              onClick={onDeleteRequest}
              aria-label="Delete notification retry config"
              disabled={saving}
            >
              <Trash2
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
              />
              <span>Delete</span>
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{DEFAULT_MAX_RETRY_COUNT}</td>
      <td>{DEFAULT_RETRY_INTERVAL_MINUTES}</td>
      <td className="esc-table-actions-col esc-table-actions-col--wide">
        <div className="esc-table-actions">
          <button
            type="button"
            className="esc-icon-btn"
            onClick={onCreateFromDefaults}
            aria-label="Create notification retry config from defaults"
            disabled={saving}
            title="Create a custom configuration"
          >
            <Pencil
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            <span>Edit</span>
          </button>
          <button
            type="button"
            className="esc-icon-btn esc-icon-btn--danger"
            onClick={onDeleteRequest}
            aria-label="Delete notification retry config"
            disabled={saving}
          >
            <Trash2
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            <span>Delete</span>
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function NotificationRetrySection({
  facilityId,
}: Readonly<NotificationRetrySectionProps>) {
  const [record, setRecord] = useState<NotificationRetryRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [maxRetryInput, setMaxRetryInput] = useState('');
  const [intervalInput, setIntervalInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await notificationRetryConfigService.list(facilityId);
    if (!result.success) {
      setRecord(null);
      setLoadError(result.error || 'Failed to load notification retry config');
      setLoading(false);
      return;
    }
    const first = result.data?.[0];
    setRecord(first ? mapApiRecord(first) : null);
    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const openCreateModal = () => {
    setIsEditing(false);
    setMaxRetryInput('3');
    setIntervalInput('5');
    setFieldError(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    if (!record) {
      return;
    }
    setIsEditing(true);
    setMaxRetryInput(String(record.maxRetryCount));
    setIntervalInput(String(record.retryIntervalMinutes));
    setFieldError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }
    setModalOpen(false);
    setFieldError(null);
  };

  const handleSave = useCallback(async () => {
    const maxRetryCount = parseRetryCount(maxRetryInput);
    const retryIntervalMinutes = parseRetryInterval(intervalInput);
    if (maxRetryCount == null || retryIntervalMinutes == null) {
      setFieldError(
        `Enter valid values: retry count ${MIN_MAX_RETRY_COUNT}-${MAX_MAX_RETRY_COUNT}, interval ${MIN_RETRY_INTERVAL_MINUTES}-${MAX_RETRY_INTERVAL_MINUTES} minutes.`
      );
      return;
    }

    setSaving(true);
    setFieldError(null);

    const payload = { facilityId, maxRetryCount, retryIntervalMinutes };
    const result =
      isEditing && record
        ? await notificationRetryConfigService.update(record.id, payload)
        : await notificationRetryConfigService.create(payload);

    setSaving(false);

    if (!result.success || !result.data) {
      setFieldError(result.error || 'Failed to save notification retry config');
      return;
    }

    setRecord(mapApiRecord(result.data));
    setModalOpen(false);
  }, [facilityId, intervalInput, isEditing, maxRetryInput, record]);

  const handleDelete = async () => {
    if (!record) {
      return;
    }
    setSaving(true);
    const result = await notificationRetryConfigService.remove(record.id, facilityId);
    setSaving(false);
    if (!result.success) {
      setLoadError(result.error || 'Failed to delete notification retry config');
      setDeleteConfirmOpen(false);
      return;
    }
    setRecord(null);
    setDeleteConfirmOpen(false);
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Notification retry</h2>
          <span className="esc-count-badge">{record ? 1 : 0}</span>
        </div>
        <p className="esc-step-subtitle">
          Configure automatic retry for SMS and WhatsApp patient notifications. Dual-channel events
          retry each failed channel independently; SMS-only events retry SMS only. Applies to report
          dispatch, appointments, no-show, critical results, OTP, and other radiology notifications.
        </p>
      </header>

      {loadError ? (
        <p
          className="esc-field-error"
          role="alert"
        >
          {loadError}
        </p>
      ) : null}

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={openCreateModal}
            disabled={Boolean(record) || loading || saving}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add retry configuration
          </button>
        </div>
      </div>

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th scope="col">Max retries</th>
              <th scope="col">Interval (minutes)</th>
              <th
                scope="col"
                className="esc-table-actions-col esc-table-actions-col--wide"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <NotificationRetryTableBody
              loading={loading}
              record={record}
              saving={saving}
              onEdit={openEditModal}
              onDeleteRequest={() => setDeleteConfirmOpen(true)}
              onCreateFromDefaults={openCreateModal}
            />
          </tbody>
        </table>
        {!loading && !record ? (
          <p className="esc-field-helper esc-table-footnote">
            No custom configuration — values above are system defaults (3 retries, 5 min interval).
          </p>
        ) : null}
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={open => !open && closeModal()}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {isEditing ? 'Edit notification retry' : 'Add notification retry'}
            </DialogTitle>
          </DialogHeader>

          <div className="esc-activate-dialog-body">
            <label
              className="esc-field"
              htmlFor="notification-retry-count"
            >
              <span className="esc-field-label">Max retry count</span>
              <input
                id="notification-retry-count"
                className={['esc-input', fieldError ? 'esc-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={maxRetryInput}
                onChange={e => {
                  setMaxRetryInput(sanitizeIntegerInput(e.target.value));
                  if (fieldError) {
                    setFieldError(null);
                  }
                }}
                onKeyDown={blockInvalidNumericKeys}
                disabled={saving}
              />
              <span className="esc-field-helper">
                Set to 0 to disable automatic retries after the first failed attempt (only the
                initial send is attempted).
              </span>
            </label>

            <label
              className="esc-field"
              htmlFor="notification-retry-interval"
            >
              <span className="esc-field-label">Retry interval (minutes)</span>
              <input
                id="notification-retry-interval"
                className={['esc-input', fieldError ? 'esc-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={intervalInput}
                onChange={e => {
                  setIntervalInput(sanitizeIntegerInput(e.target.value));
                  if (fieldError) {
                    setFieldError(null);
                  }
                }}
                onKeyDown={blockInvalidNumericKeys}
                disabled={saving}
              />
            </label>

            {fieldError ? (
              <p
                className="esc-field-error"
                role="alert"
              >
                {fieldError}
              </p>
            ) : null}
          </div>

          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="esc-btn esc-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {record ? 'Delete notification retry config?' : 'No configuration to delete'}
            </DialogTitle>
          </DialogHeader>
          {record ? (
            <p>System defaults will apply until a new configuration is created.</p>
          ) : (
            <p>
              There is no custom retry configuration for this facility. System defaults (
              {DEFAULT_MAX_RETRY_COUNT} retries, {DEFAULT_RETRY_INTERVAL_MINUTES} min interval) are
              already in use.
            </p>
          )}
          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={saving}
            >
              {record ? 'Cancel' : 'Close'}
            </button>
            {record ? (
              <button
                type="button"
                className="esc-btn esc-btn--danger study-assignment-delete-confirm-btn"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
